import { X, Check, AlertCircle, FileText } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    name: string;
    value?: string | null;
    type: 'field' | 'document';
    date?: string;
    source?: string;
  };
  onApprove?: () => void;
  onReject?: (reason: string) => void;
}

export function ReviewModal({ isOpen, onClose, item, onApprove, onReject }: ReviewModalProps) {
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (decision === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);

    setTimeout(() => {
      if (decision === 'approve') {
        toast.success(`${item.name} approved successfully`);
        onApprove?.();
      } else if (decision === 'reject') {
        toast.success(`${item.name} rejected - Owner will be notified`);
        onReject?.(rejectionReason);
      }

      setProcessing(false);
      setDecision(null);
      setRejectionReason('');
      onClose();
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full border dark:border-white/8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b dark:border-white/8">
          <div>
            <h2 className="text-xl font-semibold dark:text-white">Review {item.type === 'field' ? 'Field' : 'Document'}</h2>
            <p className="text-sm text-gray-600 dark:text-white/70 mt-1">{item.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 dark:text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Item Preview */}
          <div className="bg-gray-50 dark:bg-[#0F0E16] border dark:border-white/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              {item.type === 'document' ? (
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {item.name}
                </div>
                {item.value && (
                  <div className="text-sm text-gray-700 dark:text-white/80 mb-2">
                    Value: {item.value}
                  </div>
                )}
                {item.date && item.source && (
                  <div className="text-xs text-gray-600 dark:text-white/70">
                    Submitted {new Date(item.date).toLocaleDateString()} • By {item.source}
                  </div>
                )}
                {item.type === 'document' && (
                  <div className="mt-3">
                    <button className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                      View Full Document →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Review Decision */}
          <div className="space-y-3 mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-2">
              Review Decision
            </label>
            
            <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-white/5 ${
              decision === 'approve' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-300 dark:border-white/20'
            }">
              <input
                type="radio"
                name="decision"
                checked={decision === 'approve'}
                onChange={() => setDecision('approve')}
                className="w-4 h-4 text-green-600"
              />
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Approve</div>
                  <div className="text-xs text-gray-600 dark:text-white/70">
                    Mark as verified and compliant
                  </div>
                </div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-white/5 ${
              decision === 'reject' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-white/20'
            }">
              <input
                type="radio"
                name="decision"
                checked={decision === 'reject'}
                onChange={() => setDecision('reject')}
                className="w-4 h-4 text-red-600"
              />
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Reject</div>
                  <div className="text-xs text-gray-600 dark:text-white/70">
                    Request resubmission with corrections
                  </div>
                </div>
              </div>
            </label>
          </div>

          {/* Rejection Reason */}
          {decision === 'reject' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-2">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                placeholder="Explain what needs to be corrected..."
              />
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-3">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>Note:</strong> All review decisions are logged on the blockchain. 
              The pet owner will be notified of your decision.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16]">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-white/20 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg text-sm dark:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!decision || processing}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 text-sm"
          >
            {processing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Submit Review
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
